import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useAuth } from './useAuth';

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const { sendPasswordReset } = useAuth();

  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      await sendPasswordReset(email);
    } catch {
      // Swallowed on purpose — see the message below.
    } finally {
      setSubmitting(false);
      setSent(true);
    }
  };

  return (
    <main className="flex min-h-dvh items-center justify-center p-6">
      <form
        onSubmit={(e) => void onSubmit(e)}
        className="flex w-full max-w-sm flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
      >
        <h1 className="text-base font-semibold text-slate-900">{t('auth.resetTitle')}</h1>

        {sent ? (
          // Always the same response, sent or not: confirming which addresses exist
          // would turn this form into an account enumeration tool.
          <Alert tone="success">{t('auth.resetSent')}</Alert>
        ) : (
          <>
            <p className="text-sm text-slate-600">{t('auth.resetHelp')}</p>
            <Input
              label={t('auth.email')}
              type="email"
              name="email"
              autoComplete="username"
              required
              dir="ltr"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Button type="submit" loading={submitting}>
              {t('auth.resetSend')}
            </Button>
          </>
        )}

        <Link to="/login" className="text-center text-xs text-brand-700 hover:underline">
          {t('auth.backToSignIn')}
        </Link>
      </form>
    </main>
  );
}
